import moment from 'moment';
import { isValidCron } from 'cron-validator';
import i18n from '../i18n/i18n';
import {datetimeFormat} from '../config/moment';

const requiredIf = (required, {message} = {message: ''}) => ({
  required,
  message: message || i18n.t('errors.messages.blank'),
});

const email = {type: 'email', message: i18n.t('errors.messages.email')};

const url = {type: 'url', message: i18n.t('errors.messages.url')};

const toObject = (val) => {
  if (typeof val !== 'object') {
    try {
      val = JSON.parse(val);
    } catch (e) {
      return val;
    }
  }
  return val;
};

const toNumber = (val) => (typeof val === 'number' ? val : parseFloat(val));

const object = {type: 'object', message: i18n.t('errors.messages.object'), transform: toObject};

const positiveNumber = {
  type: 'number',
  min: 1,
  transform: (val) => parseFloat(val),
  message: i18n.t('errors.messages.mustBeAPositiveNumber'),
};

const greaterThanOrEqualTo = (min) => ({
  type: 'number',
  validator: (rule, value) => {
    if (value >= min || !value) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(i18n.t('errors.messages.mustBeMinOrGreater', {min})));
  },
  transform: toNumber,
});

const greaterThan = (min) => ({
  type: 'number',
  validator: (rule, value) => {
    if (value > min || !value) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(i18n.t('errors.messages.mustBeGreaterThanMin', {min})));
  },
  transform: toNumber,
});

const lessThanOrEqualTo = (max) => ({
  type: 'number',
  validator: (rule, value) => {
    if (value <= max || !value) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(i18n.t('errors.messages.mustBeMaxOrLess', {max})));
  },
  transform: toNumber,
});

const lessThan = (max) => ({
  type: 'number',
  validator: (rule, value) => {
    if (value < max || !value) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(i18n.t('errors.messages.mustBeLessThanMax', {max})));
  },
  transform: toNumber,
});

const maxDecimalPlaces = (decimals) => ({
  type: 'number',
  validator: (rule, value) => {
    if (!value) {
      return Promise.resolve();
    }
    const parts = new Intl.NumberFormat().formatToParts(value);
    const decimalLocation = parts.findIndex((part) => part.type === 'decimal');
    if (decimalLocation === -1 || decimalLocation === parts.length - 1
      || parts[decimalLocation + 1].value.length <= decimals) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(i18n.t('errors.messages.maxDecimalPlaces', {decimalPlaces: decimals})));
  },
});

const minDecimalPlaces = (decimals) => ({
  type: 'number',
  validator: (rule, value) => {
    if (!value) {
      return Promise.resolve();
    }
    const parts = new Intl.NumberFormat().formatToParts(value);
    const decimalLocation = parts.findIndex((part) => part.type === 'decimal');
    if (decimalLocation === -1 || decimalLocation === parts.length - 1
      || parts[decimalLocation + 1].value.length < decimals) {
      return Promise.reject(new Error(i18n.t('errors.messages.minDecimalPlaces', {decimalPlaces: decimals})));
    }
    return Promise.resolve();
  },
});

const integer = {
  type: 'number',
  validator: (rule, value) => {
    if (value % 1 === 0) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(i18n.t('errors.messages.mustBeAnInteger')));
  },
  transform: toNumber,
};

// eslint-disable-next-line no-unused-vars
const dateRangeValidatorIf = ({checkFromValid = true, checkToValid = true, condition} = {}) => ({getFieldValue}) => ({
  validator(rule, value) {
    if (condition) {
      if (value) {
        if (checkFromValid && checkToValid) {
          if (moment(value[0]).isValid() && moment(value[1]).isValid()) {
            return Promise.resolve();
          }
        } else if (checkFromValid && !checkToValid) {
          if (moment(value[0]).isValid()) {
            return Promise.resolve();
          }
          return Promise.reject(new Error(i18n.t('errors.messages.fromDateRequired')));
        } else if (!checkFromValid && checkToValid) {
          if (moment(value[1]).isValid()) {
            return Promise.resolve();
          }
          return Promise.reject(new Error(i18n.t('errors.messages.toDateRequired')));
        }
      }

      return Promise.reject(new Error(i18n.t('errors.messages.dateRangeRequired')));
    }

    return Promise.resolve();
  },
});

/**
 * @param maxSize Define max file size in mb
 */
const maxFileSize = (maxSize) => ({
  type: 'object',
  validator: (rule, file) =>
    (file?.size / 1024 / 1024 > maxSize
      ? Promise.reject(new Error(i18n.t('errors.messages.maxFileSize', {maxFileSize: maxSize})))
      : Promise.resolve()),
});

const uniqueKeys = () => ({
  validator: (rule, value) => {
    if (value) {
      const trimmedValue = value.replace(/\s(?=(?:[^'"`]*(['"`])[^'"`]*\1)*[^'"`]*$)/g, '');
      const valueObject = toObject(value);
      if (typeof valueObject === 'object') {
        if (trimmedValue.length > JSON.stringify(valueObject).length) {
          return Promise.reject(new Error(i18n.t('errors.messages.uniqueKeys')));
        }
      }
    }
    return Promise.resolve();
  },
});

const stringMinLength = (length) => ({
  min: length,
  message: i18n.t('errors.messages.minLength', {length}),
});

const stringMaxLength = (length) => ({
  max: length,
  message: i18n.t('errors.messages.maxLength', {length}),
});

const maxSelectCount = (maxLength) => ({
  type: 'array',
  validator: (rule, value) => {
    if (value.length <= maxLength) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(i18n.t('errors.messages.maxLengthOrLessMustBeSelected', {maxLength})));
  },
});

const isSameOrAfter = (minDate) => {
  if (!minDate) {
    return Promise.resolve();
  }
  return ({
    validator: (rule, value) => {
      if (value && !value.isSameOrAfter(minDate)) {
        return Promise.reject(
          new Error(i18n.t('errors.messages.mustBeSameOrAfter', {minDate: minDate.format(datetimeFormat)}))
        );
      }
      return Promise.resolve();
    },
  });
};

const isAfter = (minDate) => {
  if (!minDate) {
    return Promise.resolve();
  }
  return ({
    validator: (rule, value) => {
      if (value && !value.isAfter(minDate)) {
        return Promise.reject(
          new Error(i18n.t('errors.messages.mustBeAfter', {minDate: minDate.format(datetimeFormat)}))
        );
      }
      return Promise.resolve();
    },
  });
};

const isSameOrBefore = (maxDate) => {
  if (!maxDate) {
    return Promise.resolve();
  }
  return ({
    validator: (rule, value) => {
      if (value && !value.isSameOrBefore(maxDate)) {
        return Promise.reject(
          new Error(i18n.t('errors.messages.mustBeSameOrBefore', {maxDate: maxDate.format(datetimeFormat)}))
        );
      }
      return Promise.resolve();
    },
  });
};

const isBefore = (maxDate) => {
  if (!maxDate) {
    return Promise.resolve();
  }
  return ({
    validator: (rule, value) => {
      if (value && !value.isBefore(maxDate)) {
        return Promise.reject(
          new Error(i18n.t('errors.messages.mustBeBefore', {maxDate: maxDate.format(datetimeFormat)}))
        );
      }
      return Promise.resolve();
    },
  });
};

const validCron = {
  validator: (rule, value) => {
    if (!value || isValidCron(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(i18n.t('errors.messages.isNotValidCron')));
  },
};

const number = {
  validator: (rule, v) => {
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(v)) {
      return Promise.reject(new Error(i18n.t('error.messages.notNumber')));
    }
    return Promise.resolve();
  },
};

export const ValidationRules = {
  required: requiredIf(true),
  requiredIf,
  email,
  positiveNumber,
  maxNumber: lessThanOrEqualTo,
  lessThanOrEqualTo,
  lessThan,
  minNumber: greaterThanOrEqualTo,
  greaterThanOrEqualTo,
  greaterThan,
  maxDecimalPlaces,
  minDecimalPlaces,
  integer,
  url,
  object,
  dateRangeValidatorIf,
  maxFileSize,
  uniqueKeys,
  stringMinLength,
  stringMaxLength,
  maxSelectCount,
  isSameOrAfter,
  isAfter,
  isSameOrBefore,
  isBefore,
  validCron,
  number,
};
